import type { ColumnOrderState, ColumnPinningState, ColumnSizingState, VisibilityState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { GridSpacing } from '../components/Grid';
import { clearTableSettings, getTableSettings, saveTableSettings, type TableSettings } from '../utils';

export type UseTableSettingsOptions = {
  /**
   * Unique identifier for the table. If not provided, settings will not be persisted.
   */
  id?: string;
  /**
   * Debounce delay in milliseconds for saving settings to localStorage.
   * Defaults to 300ms.
   */
  debounceMs?: number;
};

export type UseTableSettingsReturn = {
  /**
   * Initial spacing value from localStorage, or undefined if not set.
   */
  initialSpacing: GridSpacing | undefined;
  /**
   * Initial page size from localStorage, or undefined if not set.
   */
  initialPageSize: number | undefined;
  /**
   * Initial column order from localStorage, or undefined if not set.
   */
  initialColumnOrder: ColumnOrderState | undefined;
  /**
   * Initial column pinning from localStorage, or undefined if not set.
   */
  initialColumnPinning: ColumnPinningState | undefined;
  /**
   * Initial column visibility from localStorage, or undefined if not set.
   */
  initialColumnVisibility: VisibilityState | undefined;
  /**
   * Initial column sizing from localStorage, or undefined if not set.
   */
  initialColumnSizing: ColumnSizingState | undefined;
  /**
   * Save current settings to localStorage. This function is debounced.
   * Only saves if an id is provided.
   */
  saveSettings: (settings: {
    spacing?: GridSpacing;
    pageSize?: number;
    columnOrder?: ColumnOrderState;
    columnPinning?: ColumnPinningState;
    columnVisibility?: VisibilityState;
    columnSizing?: ColumnSizingState;
  }) => void;
  /**
   * Clear all settings from localStorage for this table.
   */
  clearSettings: () => void;
};

/**
 * Custom hook for managing table settings persistence in localStorage.
 *
 * This hook implements a write-only persistence pattern:
 * - Reads settings from localStorage once on mount (if id is provided)
 * - Provides initial values for state initialization
 * - Provides a debounced save function for persisting changes
 * - Does not sync changes back from localStorage during the session
 *
 * @example
 * ```tsx
 * const { initialSpacing, initialPageSize, saveSettings } = useTableSettings({ id: 'my-table' });
 *
 * const [spacing, setSpacing] = useState(initialSpacing ?? 'cozy');
 * const [pagination, setPagination] = useState(() => ({
 *   pageIndex: 0,
 *   pageSize: initialPageSize ?? DEFAULT_PAGE_SIZE,
 * }));
 *
 * useEffect(() => {
 *   saveSettings({ spacing, pageSize: pagination.pageSize });
 * }, [spacing, pagination.pageSize, saveSettings]);
 * ```
 */
export function useTableSettings({ id, debounceMs = 300 }: UseTableSettingsOptions = {}): UseTableSettingsReturn {
  // Read initial settings once on mount
  const initialSettings = useMemo(() => {
    if (!id) {
      return null;
    }
    return getTableSettings(id);
  }, [id]);

  // Extract initial values
  const initialSpacing = initialSettings?.spacing;
  const initialPageSize = initialSettings?.pageSize;
  const initialColumnOrder = initialSettings?.columnOrder;
  const initialColumnPinning = initialSettings?.columnPinning;
  const initialColumnVisibility = initialSettings?.columnVisibility;
  const initialColumnSizing = initialSettings?.columnSizing;

  // Use ref to store the debounce timeout
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSettingsRef = useRef<TableSettings | null>(null);

  // Debounced save function
  const saveSettings = useCallback(
    (settings: {
      spacing?: GridSpacing;
      pageSize?: number;
      columnOrder?: ColumnOrderState;
      columnPinning?: ColumnPinningState;
      columnVisibility?: VisibilityState;
      columnSizing?: ColumnSizingState;
    }) => {
      if (!id) {
        return;
      }

      // Merge with any pending settings
      const mergedSettings: TableSettings = {
        ...pendingSettingsRef.current,
        ...settings,
      };

      // Remove undefined values to keep localStorage clean
      const cleanedSettings: TableSettings = {};
      if (mergedSettings.spacing !== undefined) {
        cleanedSettings.spacing = mergedSettings.spacing;
      }
      if (mergedSettings.pageSize !== undefined) {
        cleanedSettings.pageSize = mergedSettings.pageSize;
      }
      if (mergedSettings.columnOrder !== undefined) {
        cleanedSettings.columnOrder = mergedSettings.columnOrder;
      }
      if (mergedSettings.columnPinning !== undefined) {
        cleanedSettings.columnPinning = mergedSettings.columnPinning;
      }
      if (mergedSettings.columnVisibility !== undefined) {
        cleanedSettings.columnVisibility = mergedSettings.columnVisibility;
      }
      if (mergedSettings.columnSizing !== undefined) {
        cleanedSettings.columnSizing = mergedSettings.columnSizing;
      }

      pendingSettingsRef.current = cleanedSettings;

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        if (pendingSettingsRef.current) {
          saveTableSettings(id, pendingSettingsRef.current);
          pendingSettingsRef.current = null;
        }
      }, debounceMs);
    },
    [id, debounceMs],
  );

  // Clear settings function
  const clearSettings = useCallback(() => {
    if (!id) {
      return;
    }

    // Clear any pending saves
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    pendingSettingsRef.current = null;

    // Clear from localStorage
    clearTableSettings(id);
  }, [id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Save any pending settings before unmount
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (pendingSettingsRef.current && id) {
        saveTableSettings(id, pendingSettingsRef.current);
      }
    };
  }, [id]);

  return {
    initialSpacing,
    initialPageSize,
    initialColumnOrder,
    initialColumnPinning,
    initialColumnVisibility,
    initialColumnSizing,
    saveSettings,
    clearSettings,
  };
}
